import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import {
  Brain, Zap, Activity, HeartPulse, Baby, Moon,
  ChevronRight, AlertTriangle, FileText, Stethoscope, ArrowRight,
} from "lucide-react";
import { useAuth } from "../lib/auth";

// Conditions treated — curated to match what Indian neurologists +
// neurosurgeons routinely handle. Each entry has a short pitch on the
// landing card AND a long-form info panel that opens on click. Clicking
// "Get a second opinion" deep-links into the booking wizard with the tag
// pre-filled (and the patient is sent to /login first if they aren't
// signed in yet).
const CONDITIONS = [
  {
    icon: Brain, label: "Brain Tumor", color: "from-rose-500 to-pink-600", tag: "brain_tumor",
    desc: "MRI review, surgical vs. watchful",
    blurb: "A brain tumor — benign or malignant — is one of the most frightening diagnoses a family can hear. The right second opinion can change the entire treatment plan.",
    whatItIs: "Tumors of the brain or skull base range from slow-growing meningiomas (often safe to watch) to high-grade gliomas needing urgent surgery + radiation. Imaging interpretation is everything.",
    whenSecondOpinion: [
      "You've been told 'we should operate this week' — but want to be sure",
      "Your MRI shows a mass and you don't fully understand what it means",
      "You're weighing surgery vs. radiation vs. watchful waiting",
      "You want a senior neurosurgeon to review the films before consenting to surgery",
    ],
    redFlags: ["Sudden severe headache + vomiting", "New seizures in an adult", "Progressive weakness on one side", "Personality / vision changes"],
  },
  {
    icon: Zap, label: "Epilepsy & Seizures", color: "from-amber-500 to-orange-600", tag: "epilepsy",
    desc: "Seizure work-up, medication review",
    blurb: "Most epilepsy can be controlled — but only if the seizure type, EEG and MRI are interpreted together. A second opinion can stop wrong-drug, wrong-dose years.",
    whatItIs: "Epilepsy is a tendency to recurrent unprovoked seizures. Treatment depends on whether they're focal or generalized, the EEG pattern and any structural cause on MRI.",
    whenSecondOpinion: [
      "Seizures that aren't controlled despite 2+ medications",
      "You're being told you'll need lifelong drugs after a single episode",
      "Side-effects of current medication are unbearable",
      "Considering surgical evaluation for drug-resistant epilepsy",
    ],
    redFlags: ["Seizure lasting >5 minutes (status epilepticus — call 108)", "Multiple seizures in a row", "First-ever seizure in someone over 30"],
  },
  {
    icon: HeartPulse, label: "Stroke", color: "from-red-500 to-rose-600", tag: "stroke",
    desc: "Post-stroke care, secondary prevention",
    blurb: "Surviving a stroke is just the start. The next 90 days decide your recovery — and your risk of a second one. Get the plan right.",
    whatItIs: "Strokes are either ischaemic (blocked artery, ~80%) or haemorrhagic (bleed). Recovery depends on early rehab, blood-pressure control, blood thinners, and treating the underlying cause (clot, AFib, carotid disease).",
    whenSecondOpinion: [
      "Recently discharged after a stroke and unsure about medications",
      "Carotid ultrasound or echo findings need explaining",
      "You're told 'further recovery isn't possible' — but want a fresh look",
      "Recurrent TIAs (mini-strokes) despite treatment",
    ],
    redFlags: ["Sudden weakness on one side", "Slurred speech", "Loss of vision in one eye", "Worst headache of your life — call 108 IMMEDIATELY"],
  },
  {
    icon: Activity, label: "Spine & Back Pain", color: "from-indigo-500 to-blue-600", tag: "spine",
    desc: "Disc, sciatica, surgery opinion",
    blurb: "Up to 40% of recommended spine surgeries are unnecessary. Before you go under the knife, get a senior neurosurgeon's view of your MRI.",
    whatItIs: "Most disc and back pain heals with time, physiotherapy and targeted injections. Surgery is reserved for true neurological compromise — not for pain alone.",
    whenSecondOpinion: [
      "Surgery has been recommended — and you want to be 100% sure",
      "Pain or numbness has lasted longer than 6 weeks",
      "You're considering an artificial disc / fusion / minimally invasive option",
      "Failed back-surgery syndrome — pain returned after a previous operation",
    ],
    redFlags: ["Loss of bladder / bowel control (cauda equina — emergency!)", "Progressive leg weakness", "Saddle numbness", "Fever + back pain"],
  },
  {
    icon: Baby, label: "Pediatric Neurology", color: "from-emerald-500 to-teal-600", tag: "pediatric",
    desc: "Developmental delay, childhood seizures",
    blurb: "Children's brains are different. We connect you to specialists who treat seizures, developmental delay and headaches in children, every day.",
    whatItIs: "Pediatric neurology covers seizures, autism / developmental delay, cerebral palsy, ADHD with neurological signs, and rare genetic conditions. Early intervention changes lifelong outcomes.",
    whenSecondOpinion: [
      "Your child has been diagnosed with epilepsy or ADHD and you want clarity",
      "Developmental milestones are delayed and you've been told to 'wait and watch'",
      "Recurrent headaches / abdominal pain in a school-going child",
      "Recently started anti-seizure medication and unsure about it",
    ],
    redFlags: ["Seizure in a baby under 6 months", "Sudden loss of milestones already gained", "Persistent vomiting + drowsiness", "Bulging fontanelle in an infant"],
  },
  {
    icon: Moon, label: "Headaches & Migraine", color: "from-violet-500 to-purple-600", tag: "headache",
    desc: "Chronic migraine, cluster headaches",
    blurb: "If you're popping painkillers more than twice a week, you're probably making your headaches worse. A neurologist can break the cycle.",
    whatItIs: "Most adult headaches are migraine, tension-type, or medication-overuse. The right preventative — anti-CGRPs, beta-blockers, topiramate or just lifestyle — can reduce attacks by 70-80%.",
    whenSecondOpinion: [
      "Migraines for years that aren't responding to over-the-counter meds",
      "Headaches >15 days/month (chronic migraine)",
      "Side-effects from current preventatives",
      "Cluster headaches — short, severe, around one eye",
    ],
    redFlags: ["Sudden 'thunderclap' headache (call 108)", "Headache + fever + stiff neck", "Headache that wakes you from sleep", "First severe headache after age 50"],
  },
];

export default function ConditionsSection() {
  const [openTag, setOpenTag] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();
  const active = CONDITIONS.find((c) => c.tag === openTag);

  const startOpinion = (tag) => {
    if (user && user.role === "patient") {
      nav(`/patient?tag=${encodeURIComponent(tag)}`);
    } else {
      nav(`/login?as=patient&tag=${encodeURIComponent(tag)}`);
    }
    setOpenTag(null);
  };

  return (
    <section className="bg-white border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="max-w-2xl">
          <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[#0B5ED7]">Conditions we review</div>
          <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-semibold text-slate-900 leading-tight">
            Common neuro concerns, <span className="gradient-text">answered clearly</span>.
          </h2>
          <p className="mt-3 text-slate-600 text-sm sm:text-base leading-relaxed">
            Tap a condition to read what it really means and when a second opinion can change your treatment plan.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-5">
          {CONDITIONS.map((c) => (
            <Card
              key={c.tag}
              role="button"
              tabIndex={0}
              onClick={() => setOpenTag(c.tag)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpenTag(c.tag); } }}
              className="group p-5 border-slate-200 hover:border-[#0B5ED7]/40 hover:shadow-md transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#0B5ED7]/40"
              data-testid={`condition-${c.tag}`}
            >
              <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${c.color} text-white grid place-items-center shadow-sm group-hover:scale-105 transition`}>
                <c.icon className="w-5 h-5" />
              </div>
              <div className="mt-4 font-heading font-semibold text-slate-900 text-sm sm:text-base flex items-center gap-1">
                {c.label}
                <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-[#0B5ED7] group-hover:translate-x-0.5 transition" />
              </div>
              <div className="text-[11px] sm:text-xs text-slate-500 mt-1 line-clamp-2">{c.desc}</div>
              <div className="mt-3 text-[11px] font-semibold text-[#0B5ED7] flex items-center gap-1 opacity-0 group-hover:opacity-100 transition" data-testid={`condition-${c.tag}-cta-hint`}>
                Read more · get opinion <ArrowRight className="w-3 h-3" />
              </div>
            </Card>
          ))}
        </div>

        <p className="mt-8 text-xs text-slate-400 italic">
          Don't see your condition? Our specialists still welcome your case — tap Book Consultation and describe your situation.
        </p>

        <div className="mt-6">
          <a
            href="/conditions"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal-700 hover:text-teal-800"
            data-testid="browse-all-conditions-link"
          >
            Browse the full Health Library → all 8 conditions
          </a>
        </div>
      </div>

      <Dialog open={!!openTag} onOpenChange={(o) => !o && setOpenTag(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="condition-modal">
          {active && (
            <>
              <DialogHeader>
                <div className="flex items-start gap-4">
                  <div className={`w-14 h-14 shrink-0 rounded-2xl bg-gradient-to-br ${active.color} text-white grid place-items-center shadow-md`}>
                    <active.icon className="w-7 h-7" />
                  </div>
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#0B5ED7]">Condition</div>
                    <DialogTitle className="font-heading text-2xl text-slate-900 mt-0.5" data-testid="condition-modal-title">
                      {active.label}
                    </DialogTitle>
                    <DialogDescription className="text-sm text-slate-600 mt-1">
                      {active.blurb}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <div className="mt-2 space-y-5">
                <section>
                  <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 flex items-center gap-1.5">
                    <FileText className="w-3.5 h-3.5" /> What it is
                  </div>
                  <p className="mt-1.5 text-sm text-slate-700 leading-relaxed">{active.whatItIs}</p>
                </section>

                <section>
                  <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 flex items-center gap-1.5">
                    <Stethoscope className="w-3.5 h-3.5" /> When a second opinion really helps
                  </div>
                  <ul className="mt-2 space-y-1.5">
                    {active.whenSecondOpinion.map((item, i) => (
                      <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#0B5ED7] shrink-0"/>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </section>

                <section className="rounded-xl border border-red-200 bg-red-50 p-4">
                  <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-red-800 flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5" /> Red flags — seek emergency care
                  </div>
                  <ul className="mt-2 space-y-1">
                    {active.redFlags.map((rf, i) => (
                      <li key={i} className="text-xs text-red-900 flex items-start gap-2">
                        <span className="mt-1 w-1 h-1 rounded-full bg-red-500 shrink-0"/>
                        <span>{rf}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-3 flex items-center gap-2">
                    <a href="tel:108" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-red-700 text-white text-xs font-semibold hover:bg-red-800" data-testid={`condition-${active.tag}-call-108`}>Call 108</a>
                    <a href="tel:102" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-white text-red-700 border border-red-200 text-xs font-semibold hover:bg-red-50">Call 102</a>
                  </div>
                </section>
              </div>

              <div className="mt-6 pt-4 border-t border-slate-100 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
                <p className="text-[11px] text-slate-500">
                  This is general information, not a diagnosis. A verified neurologist will review your scans and respond in 24–48&nbsp;hours.
                </p>
                <Button
                  onClick={() => startOpinion(active.tag)}
                  className="h-11 bg-[#0B5ED7] hover:bg-[#084BB0] shrink-0"
                  data-testid={`condition-${active.tag}-cta`}
                >
                  {user && user.role === "patient" ? "Get my opinion" : "Login & get opinion"}
                  <ChevronRight className="w-4 h-4 ml-1"/>
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
